import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { MenuSection, OverridePositionsMap } from 'app/models/menu.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialog } from '@angular/material/dialog';
import { EditMenuModalComponent } from '../shared/edit-menu-modal/edit-menu-modal.component';
import { Store } from '@ngrx/store';
import { setFullMenuInfo, setMenusPending, setSearchQuery } from 'app/reducers/menu.actions';
import { DishesService } from 'app/services/dishes.service';
import { Select } from '@ngxs/store';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import * as MenuSelectors from 'app/reducers/menu.selectors';

@UntilDestroy()
@Component({
  selector: 'app-menu-main',
  templateUrl: './menu-main.component.html',
  styleUrls: ['./menu-main.component.scss']
})
export class MenuMainComponent implements OnInit, OnDestroy {
  @Select(OrganizationsState.getSelectedOrganizationId) selectedOrganizationId$: Observable<string>;

  searchQuery: string = '';
  currentOrganizationId: string;
  currency: string;
  menuSections: MenuSection[] = [];
  dataIsLoaded: boolean = false;
  getMenusPending$: Observable<boolean> = this.store.select(MenuSelectors.selectMenuPending);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private store: Store,
    private dishesService: DishesService,
  ) {
    this.activatedRoute.queryParams
      .pipe(
        untilDestroyed(this),
      ).subscribe(({q}) => {
      this.searchQuery = q ?? '';
      this.store.dispatch(setSearchQuery(this.searchQuery))
    });
  }

  async ngOnInit() {
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(setMenusPending(true));
    const selectedOrganizationId = await firstValueFrom(this.selectedOrganizationId$);
    const {menuSectionId} = await firstValueFrom(this.activatedRoute.firstChild.params);
    const queryParams = await firstValueFrom(this.activatedRoute.queryParams);
    this.currentOrganizationId = selectedOrganizationId;
    const menuSectionsResponse = await this.dishesService.getMenuSections(selectedOrganizationId);
    const menuSections = menuSectionsResponse.data;
    this.menuSections = menuSections;
    const calculatedMenuSectionId = this.calculateMenuSectionId(menuSectionId, menuSections);
    if (calculatedMenuSectionId) {
      const [menuResponse, overridePositionsResponse] =
        await Promise.all([
          this.dishesService.getFullMenu(selectedOrganizationId),
          this.dishesService.getOverrides(calculatedMenuSectionId)]);
      // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
      const overridePositionsMap = new OverridePositionsMap(overridePositionsResponse.data);
      this.store.dispatch(setFullMenuInfo([menuResponse.data, menuSections, overridePositionsMap]));
      await this.router.navigate(
        ['./', calculatedMenuSectionId],
        {
          relativeTo: this.activatedRoute,
          queryParams: queryParams,
        });
    }
    else{
      this.store.dispatch(setMenusPending(false));
    }

    this.dataIsLoaded = true;
  }

  ngOnDestroy(){
    this.dialog?.closeAll();
  }

  createMenuSection(): void {
    this.dialog.open(EditMenuModalComponent, {
      data: {
        organizationId: this.currentOrganizationId,
      } as Partial<MenuSection>
    });
  }

  async changeSearchQuery(event: any) {
    this.searchQuery = event.target.value;
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: this.searchQuery ? {q: this.searchQuery} : {q: null},
      queryParamsHandling: 'merge',
    });
  }

  private calculateMenuSectionId(
    routeMenuSectionId: string | null | undefined,
    menuSections: MenuSection[]): string
  {
    let resultMenuSection: MenuSection = null;

    if (routeMenuSectionId){
      resultMenuSection = menuSections.find(ms => ms.id === routeMenuSectionId)
    }

    if (!resultMenuSection){
      resultMenuSection = menuSections.find(ms => ms.parentMenuSectionId !== null && !ms.isDeleted);
    }

    if (!resultMenuSection){
      resultMenuSection = menuSections.find(ms => !ms.isDeleted);
    }

    if (!resultMenuSection){
      resultMenuSection = menuSections.find(ms => ms.parentMenuSectionId !== null);
    }

    if (!resultMenuSection){
      resultMenuSection = menuSections[0];
    }

    return resultMenuSection?.id;
  }
}
